import { motion } from "framer-motion";
import { HeartHandshake, Briefcase, Mountain, Users } from "lucide-react";

const offerings = [
  {
    icon: HeartHandshake,
    title: "1:1 Support",
    desc: "Personal guided sessions to awaken your inner self and align with your original resonance.",
  },
  {
    icon: Briefcase,
    title: "Business",
    desc: "Sound, frequency, and resonance collaborations for conscious brands and partnerships.",
  },
  {
    icon: Mountain,
    title: "Retreats",
    desc: "Immersive gatherings for deep restoration, remembrance, and high-frequency living.",
  },
  {
    icon: Users,
    title: "Community Gatherings",
    desc: "Heart-led circles of sound, presence, and shared resonance with fellow seekers.",
  },
];

export default function Offerings() {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="offerings" data-testid="offerings-section" className="relative py-24 md:py-32 bg-[#f7efe2]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="font-accent text-sm tracking-widest-2 uppercase text-[#c2a04b] mb-4">How we walk together</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#3a2630] leading-tight">
            Guided support to awaken & remember
          </h2>
          <p className="text-[#3a2630]/70 mt-5 leading-relaxed">
            Every offering below is shared with love and is completely free of charge.
          </p>
          <div className="inline-flex items-center gap-2 mt-6 px-5 py-2 rounded-full bg-[#e3b4b0]/30 border border-[#b76e79]/30">
            <span className="w-2 h-2 rounded-full bg-[#b76e79] animate-pulse" />
            <span data-testid="free-badge" className="font-accent text-sm tracking-wide text-[#9c5560]">
              100% free of charge — always
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {offerings.map((o, i) => (
            <motion.div
              key={o.title}
              data-testid={`offering-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="group bg-[#fbf6ec] rounded-2xl p-7 border border-[#e3b4b0]/40 hover:border-[#b76e79]/60 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(156,85,96,0.14)]"
            >
              <div className="w-14 h-14 rounded-full bg-[#e3b4b0]/30 flex items-center justify-center mb-5 group-hover:bg-[#b76e79] transition-colors duration-500">
                <o.icon size={24} className="text-[#9c5560] group-hover:text-[#f7efe2] transition-colors duration-500" />
              </div>
              <h3 className="font-display text-2xl text-[#3a2630] mb-2">{o.title}</h3>
              <span className="inline-block mb-2 font-accent text-[10px] tracking-widest-2 uppercase px-2.5 py-0.5 rounded-full bg-[#c2a04b]/20 text-[#9c7a16]">Free of charge</span>
              <p className="text-sm text-[#3a2630]/70 leading-relaxed">{o.desc}</p>
              <button
                data-testid={`offering-cta-${i}`}
                onClick={() => scrollTo("#contact")}
                className="mt-5 font-accent text-sm text-[#b76e79] hover:text-[#9c5560] tracking-wide inline-flex items-center gap-1 group/btn"
              >
                Reach out
                <span className="transition-transform duration-300 group-hover/btn:translate-x-1">→</span>
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
